import { toIsoDateTimeString } from '@labrav/common'
import { FormikProps } from 'formik'
import {
  MicrosoftCloudSettingsSections,
  type FlyoutCloudSettingsSectionsProps,
  type MicrosoftOnboarding,
  type Prerequisites,
  type PrerequisitesArray,
} from '../types'

export const prerequisitesToArray = (
  prerequisites: Prerequisites
): PrerequisitesArray => {
  return [
    { id: 'hasAccessedMpc', value: prerequisites.hasAccessedMpc },
    {
      id: 'hasEnrolledInCommercialMarketplace',
      value: prerequisites.hasEnrolledInCommercialMarketplace,
    },
    {
      id: 'hasCompletedOrganizationProfile',
      value: prerequisites.hasCompletedOrganizationProfile,
    },
  ]
}

export const prerequisitesFromArray = (
  prerequisitesArray: PrerequisitesArray
): Prerequisites => {
  const prerequisitesMap = prerequisitesArray.reduce(
    (acc, item) => {
      acc[item.id] = item.value
      return acc
    },
    {} as Record<string, boolean>
  )

  return {
    hasAccessedMpc: prerequisitesMap.hasAccessedMpc || false,
    hasEnrolledInCommercialMarketplace:
      prerequisitesMap.hasEnrolledInCommercialMarketplace || false,
    hasCompletedOrganizationProfile:
      prerequisitesMap.hasCompletedOrganizationProfile || false,
  }
}

export const convertApiResponseToReducer = (
  apiResponse: any
): MicrosoftOnboarding => {
  const draftJson = apiResponse?.draft_json || {}
  const cloudSettings = draftJson.cloud_settings || {}
  const prerequisites = cloudSettings.cloud_prerequisites || []
  const accountConfiguration = cloudSettings.account_configuration || {}
  const cloudCredentials = cloudSettings.cloud_credentials || {}
  const lockMetadata = apiResponse?.lock_metadata || {}
  const companyInfo = draftJson?.labra_partner_details || {}
  const prerequisitesObj = prerequisitesFromArray(prerequisites)

  return {
    hasAccessedMpc: prerequisitesObj.hasAccessedMpc,
    hasEnrolledInCommercialMarketplace:
      prerequisitesObj.hasEnrolledInCommercialMarketplace,
    hasCompletedOrganizationProfile:
      prerequisitesObj.hasCompletedOrganizationProfile,
    sellerType: Array.isArray(accountConfiguration.seller_type)
      ? accountConfiguration.seller_type
      : accountConfiguration.seller_type
        ? [accountConfiguration.seller_type]
        : [],
    currencies:
      accountConfiguration.currencies?.length > 0
        ? accountConfiguration.currencies
        : ['USD'],
    connectionName: cloudCredentials.connection_name || '',
    tenantId: cloudCredentials.tenant_id || '',
    clientId: cloudCredentials.client_id || '',
    clientSecret: cloudCredentials.client_secret || '',
    clientSecretExpirationDate: toIsoDateTimeString(
      cloudCredentials.client_secret_expiration_date || ''
    ),
    mpnId: cloudCredentials.mpn_id || '',
    connectionTested: 'standBy',
    lockMetadata: {
      lockedBy: lockMetadata?.locked_by || '',
      lockedByName: lockMetadata?.locked_by_name || '',
      lockByEmail: lockMetadata?.lock_by_email || '',
      windowId: lockMetadata?.window_id || '',
      lastActivityAt: lockMetadata?.last_activity_at || '',
      lockAcquisitionProbe: lockMetadata?.lock_acquisition_probe || false,
      editingEnabled: lockMetadata?.editing_enabled || false,
    },
    filledCloudSettings: cloudSettings.form_filled || false,
    filledCompanyInfo: companyInfo?.form_filled || false,
    companyName: companyInfo?.company_name || '',
    companyIndustry: companyInfo?.industry || '',
    companyWebsite: companyInfo?.company_website || '',
    acceptedTermsAndConditions: companyInfo?.accepted_terms || false,
    submit: apiResponse?.submit || false,
  }
}

export const convertMicrosoftFormToApiPayload = (
  partnerId: string,
  microsoftOnboardingForm: FlyoutCloudSettingsSectionsProps
) => {
  return {
    partner_id: partnerId,
    draft_json: {
      labra_partner_details: {
        form_filled:
          microsoftOnboardingForm[
            MicrosoftCloudSettingsSections.FILLED_COMPANY_INFO
          ],
        company_name:
          microsoftOnboardingForm[MicrosoftCloudSettingsSections.COMPANY_INFO]
            ?.companyName || '',
        company_website:
          microsoftOnboardingForm[MicrosoftCloudSettingsSections.COMPANY_INFO]
            ?.companyWebsite || '',
        industry:
          microsoftOnboardingForm[MicrosoftCloudSettingsSections.COMPANY_INFO]
            ?.companyIndustry || '',
        accepted_terms:
          microsoftOnboardingForm[MicrosoftCloudSettingsSections.COMPANY_INFO]
            ?.acceptedTermsAndConditions || false,
      },
      cloud_settings: {
        form_filled:
          microsoftOnboardingForm[
            MicrosoftCloudSettingsSections.FILLED_CLOUD_SETTINGS
          ],
        cloud_prerequisites: prerequisitesToArray(
          microsoftOnboardingForm[MicrosoftCloudSettingsSections.PREREQUISITES]
        ),
        account_configuration: {
          seller_type:
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.ACCOUNT_CONFIGURATION
            ]?.sellerType || [],
          currencies: microsoftOnboardingForm[
            MicrosoftCloudSettingsSections.ACCOUNT_CONFIGURATION
          ]?.currencies || ['USD'],
        },
        cloud_credentials: {
          connection_name:
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS
            ]?.connectionName || '',
          tenant_id:
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS
            ]?.tenantId || '',
          client_id:
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS
            ]?.clientId || '',
          client_secret:
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS
            ]?.clientSecret || '',
          client_secret_expiration_date: toIsoDateTimeString(
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS
            ]?.clientSecretExpirationDate || ''
          ),
          mpn_id:
            microsoftOnboardingForm[
              MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS
            ]?.mpnId || '',
        },
      },
    },
    lock_metadata: {
      locked_by: microsoftOnboardingForm.lockMetadata?.lockedBy,
      last_activity_at: microsoftOnboardingForm.lockMetadata?.lastActivityAt,
      lock_acquisition_probe:
        microsoftOnboardingForm.lockMetadata?.lockAcquisitionProbe,
      window_id: microsoftOnboardingForm.lockMetadata?.windowId,
      locked_by_name: microsoftOnboardingForm.lockMetadata?.lockedByName,
      lock_by_email: microsoftOnboardingForm.lockMetadata?.lockByEmail,
      editing_enabled: microsoftOnboardingForm.lockMetadata?.editingEnabled,
    },
    submit: microsoftOnboardingForm[MicrosoftCloudSettingsSections.SUBMIT],
  }
}

export const convertReducerToMicrosoftForm = (
  reducerData: MicrosoftOnboarding
): FlyoutCloudSettingsSectionsProps => {
  return {
    [MicrosoftCloudSettingsSections.PREREQUISITES]: {
      hasAccessedMpc: reducerData.hasAccessedMpc,
      hasEnrolledInCommercialMarketplace:
        reducerData.hasEnrolledInCommercialMarketplace,
      hasCompletedOrganizationProfile:
        reducerData.hasCompletedOrganizationProfile,
    },
    [MicrosoftCloudSettingsSections.ACCOUNT_CONFIGURATION]: {
      sellerType: reducerData.sellerType,
      currencies: reducerData.currencies,
    },
    [MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS]: {
      connectionName: reducerData.connectionName,
      tenantId: reducerData.tenantId,
      clientId: reducerData.clientId,
      clientSecret: reducerData.clientSecret,
      clientSecretExpirationDate: reducerData.clientSecretExpirationDate,
      mpnId: reducerData.mpnId,
      connectionTested: reducerData.connectionTested,
    },
    [MicrosoftCloudSettingsSections.STEPS]: {
      prerequisites: reducerData.hasAccessedMpc,
      accountConfiguration: reducerData.hasCompletedOrganizationProfile,
      cloudCredentials: reducerData.connectionTested === 'success',
    },
    [MicrosoftCloudSettingsSections.LOCK_METADATA]: {
      lockedBy: reducerData.lockMetadata?.lockedBy,
      lockedByName: reducerData.lockMetadata?.lockedByName,
      lockByEmail: reducerData.lockMetadata?.lockByEmail,
      windowId: reducerData.lockMetadata?.windowId,
      lastActivityAt: reducerData.lockMetadata?.lastActivityAt,
      lockAcquisitionProbe: reducerData.lockMetadata?.lockAcquisitionProbe,
      editingEnabled: reducerData.lockMetadata?.editingEnabled,
    },
    [MicrosoftCloudSettingsSections.COMPANY_INFO]: {
      companyName: reducerData.companyName,
      companyIndustry: reducerData.companyIndustry,
      companyWebsite: reducerData.companyWebsite,
      acceptedTermsAndConditions: reducerData.acceptedTermsAndConditions,
    },
    [MicrosoftCloudSettingsSections.FILLED_CLOUD_SETTINGS]:
      reducerData.filledCloudSettings,
    [MicrosoftCloudSettingsSections.FILLED_COMPANY_INFO]:
      reducerData.filledCompanyInfo,
    [MicrosoftCloudSettingsSections.SUBMIT]: reducerData.submit,
  }
}

export const touchCloudCredentialFieldsWithErrors = (
  formikProps: FormikProps<FlyoutCloudSettingsSectionsProps>
) => {
  const cloudCredentialsErrors = formikProps.errors.cloudCredentials
  if (!cloudCredentialsErrors || typeof cloudCredentialsErrors !== 'object') {
    return
  }

  Object.keys(cloudCredentialsErrors).forEach(fieldName => {
    void formikProps.setFieldTouched(
      `${MicrosoftCloudSettingsSections.CLOUD_CREDENTIALS}.${fieldName}`,
      true,
      false
    )
  })
}
